import { IPaymentMethod, IUser, SureError, TPolicy } from '../interfaces';

import api from './api';
import updateValueAtPath from './updateValueAtPath';

export function getPasswordValidations(
  password: string
): [boolean, string, string][] {
  return [
    [
      password.length >= 8,
      'At least 8 characters',
      'password_validation_max_length',
    ],
    [
      /(?=.*\d)/.test(password),
      'At least 1 number',
      'password_validation_number',
    ],
    [
      /(?=.*[a-z])/.test(password),
      'At least 1 lowercase letter',
      'password_validation_min_length',
    ],
    [
      /(?=.*[A-Z])/.test(password),
      'At least 1 uppercase letter',
      'password_validation_uppercase',
    ],
    [
      /(?=.*[@#$%^&+=!])/.test(password),
      'At least 1 special character',
      'password_validation_special_char',
    ],
  ];
}

export function isPasswordValid(password: string) {
  return /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[@#$%^&+=!])/.test(password);
}

export function changePassword(newPassword: string, oldPassword: string) {
  return api.changePassword(newPassword, oldPassword);
}

export function updatePhoneNumber(phone_number: string, user: IUser) {
  return updateValueAtPath({
    base: user,
    path: 'client.person',
    updatedValues: { ...user.client.person, phone_number },
  });
}

export type TStripeToken = {
  name: string;
  address_line1?: string;
  address_line2?: string | null;
  address_city?: string;
  address_state?: string;
  address_zip: string;
  address_country?: string;
  email: string;
};

export type TUpdatePaymentMethod = {
  createStripeToken: (
    token: TStripeToken
  ) => Promise<{ token: { id: string } }>;
  stripeTokenInfo: TStripeToken;
  policy: TPolicy;
};

export function updatePaymentMethod({
  createStripeToken,
  stripeTokenInfo,
  policy,
}: TUpdatePaymentMethod): Promise<TPolicy> {
  return new Promise((resolve, reject) => {
    return createStripeToken(stripeTokenInfo).then(({ token }) => {
      return api
        .createPaymentMethod({
          token: token.id,
          name: stripeTokenInfo.name,
          email: stripeTokenInfo.email,
        })
        .then((paymentMethod) => {
          if (paymentMethod?.details?.funding === 'prepaid') {
            return reject({
              fields: {
                prepaid_payment_method: 'Prepaid payment method cannot be used',
              },
            });
          }
          const updatedPolicy = updateValueAtPath({
            base: policy,
            path: 'payment_method',
            updatedValues: paymentMethod,
          });
          return resolve(updatedPolicy);
        })
        .catch((error) => reject(error));
    });
  });
}

export type TGetVgsPaymentMethod = {
  gatewayReferenceId: string;
  paymentServiceId: string;
  name: string;
  email: string;
};

export async function getVgsPaymentMethod({
  gatewayReferenceId,
  paymentServiceId,
  name,
  email,
}: TGetVgsPaymentMethod) {
  const paymentMethod = await api.createPaymentMethod({
    gatewayReferenceId,
    paymentServiceId,
    name,
    email,
  });
  if (paymentMethod?.details?.funding === 'prepaid') {
    throw new SureError({
      code: 'prepaid_payment_method',
      message: 'Prepaid payment method cannot be used',
    });
  }
  return paymentMethod;
}

export type TUpdatePolicyPaymentMethod = {
  policy: TPolicy;
  paymentMethod: IPaymentMethod;
};

export function updatePolicyPaymentMethod({
  policy,
  paymentMethod,
}: TUpdatePolicyPaymentMethod): TPolicy {
  const updatedPolicy = updateValueAtPath({
    base: policy,
    path: 'payment_method',
    updatedValues: paymentMethod,
  });
  return updatedPolicy;
}
